import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Alert, Box, Button, CircularProgress, Fab, FormControl, InputLabel, MenuItem, Paper, Select, Snackbar, Tab, Tabs, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { getClientFacilityProject, getClientFacilityTickets, getClientProjectPlan } from '../../../Redux/actions';
import { THEME_COLOR, buttonStyles, inputLabelFocusedStyle, selectFocusedStyle } from '../../../theme/formStyles';
import ProjectStepper from '../timeline/ProjectStepper';
import ProjectSchedulePanel from '../gantt/ProjectSchedulePanel';
import RaiseTicketDialog from '../tickets/RaiseTicketDialog';
import ClientTodayPanel from './ClientTodayPanel';
import ClientCalendar from './ClientCalendar';
import ClientVisitDialog from './ClientVisitDialog';
import ClientTicketsPanel from './ClientTicketsPanel';
import { buildIcs, downloadIcs, upcomingEvents } from './icsExport';

const tabsSx = {
    minHeight: 40,
    '& .MuiTab-root': { textTransform: 'none', fontWeight: 500, minHeight: 40, color: '#6b7785' },
    '& .Mui-selected': { color: `${THEME_COLOR} !important` },
    '& .MuiTabs-indicator': { backgroundColor: THEME_COLOR },
};

// Facility page › Project tab for client users: the project's timeline (client-visible steps only), the
// schedule chart, today's visit, the visit calendar and their tickets. Data is scoped to this facility by the server.
// The tab shows even when the facility has no project, so tickets can still be raised.
export default function ClientProjectView({ facilityId }) {
    const dispatch = useDispatch();
    const [data, setData] = useState(null);
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [projectId, setProjectId] = useState(null);
    const [tab, setTab] = useState('schedule');
    const [cursor, setCursor] = useState({ year: dayjs().year(), month: dayjs().month() });
    const [openTaskId, setOpenTaskId] = useState(null);
    const [raising, setRaising] = useState(false);
    const [notice, setNotice] = useState('');
    const [reloadKey, setReloadKey] = useState(0);

    const loadTickets = useCallback(() => {
        dispatch(getClientFacilityTickets(facilityId)).then(res => setTickets(res?.data?.success ? res.data.data || [] : []));
    }, [dispatch, facilityId]);

    const load = useCallback(({ silent = false } = {}) => {
        if (!silent) setLoading(true);
        dispatch(getClientFacilityProject(facilityId)).then((res) => {
            if (res?.data?.success) {
                setData(res.data.data);
                setError(null);
                // Default to the most recent project at this facility
                setProjectId(id => (res.data.data.projects.some(p => p.id === id) ? id : res.data.data.projects[0]?.id ?? null));
            } else {
                setError(res?.data?.message || 'Could not load the project');
            }
            setLoading(false);
        }).catch(() => { setError('Could not load the project'); setLoading(false); });
        loadTickets();
    }, [dispatch, facilityId, loadTickets]);

    useEffect(() => { load(); }, [load]);

    if (loading && !data) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress sx={{ color: THEME_COLOR }} /></Box>;
    }
    if (error && !data) {
        return <Alert severity="error" sx={{ m: 2 }} action={<Button color="inherit" size="small" onClick={() => load()}>Retry</Button>}>{error}</Alert>;
    }

    const project = data?.projects?.find(p => p.id === projectId) || data?.projects?.[0] || null;
    const openTickets = tickets.filter(t => t.stage !== 'resolved').length;

    const raiseDialog = raising && (
        <RaiseTicketDialog forClient facilityId={facilityId} projectId={project?.id} onClose={() => setRaising(false)}
            onRaised={(t, message) => { setRaising(false); setNotice(message); loadTickets(); setReloadKey(k => k + 1); setTab('tickets'); }} />
    );
    const fab = (
        <Fab variant="extended" onClick={() => setRaising(true)}
            sx={{ position: 'fixed', right: { xs: 16, md: 28 }, bottom: { xs: 16, md: 28 }, zIndex: 20, bgcolor: THEME_COLOR, color: '#fff', textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: '#006666' } }}>
            🎫 Raise a ticket
        </Fab>
    );
    const snackbar = <Snackbar open={!!notice} autoHideDuration={3500} onClose={() => setNotice('')} message={notice} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />;

    // No project at this site: tickets only
    if (!project) {
        return (
            <Box sx={{ py: 2 }}>
                <Paper sx={{ borderRadius: 3, boxShadow: 'none', border: '1px solid #eaeaea', p: { xs: 1.5, md: 2 } }}>
                    <Typography sx={{ color: '#6b7785', mb: 2 }}>There is no project for this site yet. You can still report a problem.</Typography>
                    <ClientTicketsPanel tickets={tickets} onRaise={() => setRaising(true)} />
                </Paper>
                {fab}{raiseDialog}{snackbar}
            </Box>
        );
    }

    const tasks = data.sub_tasks.filter(t => t.project_id === project.id);
    const visits = data.visits.filter(v => tasks.some(t => t.id === v.sub_task_id));
    const openTask = openTaskId ? data.sub_tasks.find(t => t.id === openTaskId) : null;
    const openRequest = openTask ? data.date_requests.find(r => r.sub_task_id === openTask.id) : null;

    const jumpToStep = (step) => {
        if (!step.start_date) return;
        const d = dayjs(step.start_date);
        setCursor({ year: d.year(), month: d.month() });
        setTab('calendar');
    };

    const exportCalendar = async () => {
        const res = await dispatch(getClientProjectPlan(project.id));
        const calibrations = res?.data?.success ? res.data.data.calibrations || [] : [];
        const events = upcomingEvents({ facilityName: data.facility.name, subTasks: tasks, tickets, calibrations });
        if (!events.length) { setNotice('No upcoming visits to add'); return; }
        downloadIcs(`${data.facility.name}-visits.ics`, buildIcs(`${data.facility.name} visits`, events));
        setNotice(`${events.length} visit${events.length === 1 ? '' : 's'} saved · open the file to add them to your calendar`);
    };

    return (
        <Box sx={{ py: 2, pb: 10 }}>
            <Paper sx={{ borderRadius: 3, boxShadow: 'none', border: '1px solid #eaeaea', p: { xs: 1.5, md: 2 }, mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap', mb: 1.5 }}>
                    <Box>
                        <Typography sx={{ fontWeight: 700, fontSize: '15px' }}>Project timeline</Typography>
                        <Typography sx={{ fontSize: '12.5px', color: '#6b7785' }}>{data.facility.name}{project.description ? ` · ${project.description}` : ''}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                        {data.projects.length > 1 && (
                            <FormControl size="small" sx={{ minWidth: 240 }}>
                                <InputLabel sx={inputLabelFocusedStyle}>Project</InputLabel>
                                <Select label="Project" value={project.id} onChange={e => setProjectId(e.target.value)} sx={selectFocusedStyle} MenuProps={{ disableScrollLock: true }}>
                                    {data.projects.map(p => <MenuItem key={p.id} value={p.id}>{p.description || `Project #${p.id}`}</MenuItem>)}
                                </Select>
                            </FormControl>
                        )}
                        <Button size="small" variant="outlined" onClick={exportCalendar} sx={buttonStyles.outlined}>📅 Add visits to my calendar</Button>
                    </Box>
                </Box>
                <ProjectStepper project={project} onStepClick={jumpToStep} />
            </Paper>

            <Paper sx={{ borderRadius: 3, boxShadow: 'none', border: '1px solid #eaeaea', px: { xs: 1.5, md: 2 }, pb: 2 }}>
                <Tabs value={tab} onChange={(e, v) => setTab(v)} variant="scrollable" allowScrollButtonsMobile sx={{ ...tabsSx, mb: 1.5, borderBottom: '1px solid #e5e9ee' }}>
                    <Tab value="schedule" label="Schedule" />
                    <Tab value="today" label="Today" />
                    <Tab value="calendar" label="Calendar" />
                    <Tab value="tickets" label={`Tickets${openTickets ? ` · ${openTickets}` : ''}`} />
                </Tabs>
                {tab === 'schedule' && (
                    <ProjectSchedulePanel forClient projectId={project.id} reloadKey={reloadKey} onMessage={setNotice}
                        onOpenSubTask={st => setOpenTaskId(st.id)} onOpenTicket={() => setTab('tickets')} />
                )}
                {tab === 'today' && <ClientTodayPanel tasks={tasks} visits={visits} onOpen={t => setOpenTaskId(t.id)} onShowCalendar={() => setTab('calendar')} />}
                {tab === 'calendar' && <ClientCalendar tasks={tasks} visits={visits} cursor={cursor} onCursorChange={setCursor} onOpen={t => setOpenTaskId(t.id)} />}
                {tab === 'tickets' && <ClientTicketsPanel tickets={tickets} onRaise={() => setRaising(true)} />}
            </Paper>

            {openTask && (
                <ClientVisitDialog key={openTask.id} task={openTask} request={openRequest}
                    onClose={() => setOpenTaskId(null)} onRequested={() => load({ silent: true })} />
            )}
            {fab}{raiseDialog}{snackbar}
        </Box>
    );
}
